import React, { useState, useEffect } from "react";
import {
  Container,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Box,
  Alert,
  Link,
  Tooltip,
} from "@mui/material";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import PersonIcon from "@mui/icons-material/Person";
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc,
} from "firebase/firestore";
import { db } from "./firebase-config";
import { getAuth } from "firebase/auth";
import "./App.css";

const Allergies = () => {
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState("");
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [noAccessMessage, setNoAccessMessage] = useState("");

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = auth.onAuthStateChanged(async (currentUser) => {
      if (currentUser) {
        // Fetch user roles
        const userDoc = await getDoc(doc(db, "users", currentUser.uid));
        if (userDoc.exists()) {
          const roles = userDoc.data().roles || [];

          // Check if user is a teacher, principal, or first-aid
          if (
            roles.includes("teacher") ||
            roles.includes("principal") ||
            roles.includes("first-aid")
          ) {
            await fetchClasses(currentUser.uid, roles);
          } else {
            setNoAccessMessage(
              "You do not have access to this page. Only teachers, principals, and first-aid can view allergies.",
            );
          }
        }
        setLoading(false);
      } else {
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  const fetchClasses = async (userId, roles) => {
    try {
      let classesData = [];

      if (roles.includes("principal") || roles.includes("first-aid")) {
        // Principal and first-aid can see all classes
        const classesSnapshot = await getDocs(collection(db, "classes"));
        classesData = classesSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
      } else if (roles.includes("teacher")) {
        // Teacher can only see classes they are assigned to
        const classesSnapshot = await getDocs(collection(db, "classes"));
        classesData = classesSnapshot.docs
          .map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }))
          .filter((cls) => cls.teachers && cls.teachers.includes(userId));
      }

      // Sort classes by name
      classesData.sort((a, b) => a.name.localeCompare(b.name));
      setClasses(classesData);
    } catch (error) {
      console.error("Error fetching classes: ", error);
    }
  };

  const fetchStudents = async (className) => {
    try {
      setStudents([]);
      if (!className) {
        return;
      }

      // Query students in the selected class with Active status
      const q = query(
        collection(db, "students"),
        where("class", "==", className),
        where("status", "==", "Active"),
      );
      const studentsSnapshot = await getDocs(q);
      let studentsData = studentsSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      // Sort students alphabetically by first name, then last name
      studentsData.sort((a, b) => {
        const aName = `${a.firstName || ""} ${a.lastName || ""}`.trim();
        const bName = `${b.firstName || ""} ${b.lastName || ""}`.trim();
        return aName.localeCompare(bName);
      });

      setStudents(studentsData);
    } catch (error) {
      console.error("Error fetching students: ", error);
    }
  };

  const handleClassChange = (event) => {
    const className = event.target.value;
    setSelectedClass(className);
    fetchStudents(className);
  };

  if (loading) {
    return (
      <Container maxWidth="lg" className="app-wrapper">
        <Typography>Loading...</Typography>
      </Container>
    );
  }

  if (noAccessMessage) {
    return (
      <Container maxWidth="lg" className="app-wrapper">
        <Alert severity="warning">{noAccessMessage}</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" className="app-wrapper">
      <Box sx={{ marginBottom: 3 }}>
        <FormControl fullWidth sx={{ maxWidth: 300 }}>
          <InputLabel id="class-select-label">Select Class</InputLabel>
          <Select
            labelId="class-select-label"
            id="class-select"
            value={selectedClass}
            onChange={handleClassChange}
            label="Select Class"
          >
            <MenuItem value="">
              <em>-- Select a Class --</em>
            </MenuItem>
            {classes.map((cls) => (
              <MenuItem key={cls.id} value={cls.name}>
                {cls.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {selectedClass && students.length === 0 && (
        <Alert severity="info">No students found in this class.</Alert>
      )}

      {selectedClass && students.length > 0 && (
        <TableContainer component={Paper}>
          <Table stickyHeader>
            <TableHead>
              <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                <TableCell sx={{ fontWeight: "bold" }}>Students</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Allergies</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {students.map((student) => (
                <TableRow key={student.id}>
                  <TableCell>
                    <Box
                      sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                    >
                      {/* Student Name */}
                      <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                        {student.firstName} {student.middleName}{" "}
                        {student.lastName}
                      </Typography>

                      {/* Father's Info */}
                      {student.fatherName && (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                          }}
                        >
                          <Tooltip title="Father">
                            <PersonIcon
                              sx={{ fontSize: "18px", color: "#1976d2" }}
                            />
                          </Tooltip>
                          <Typography variant="caption">
                            {student.fatherName}
                          </Typography>
                          {student.fatherEmail && (
                            <Tooltip title={student.fatherEmail}>
                              <Link
                                href={`mailto:${student.fatherEmail}`}
                                sx={{
                                  display: "flex",
                                  cursor: "pointer",
                                  color: "#1976d2",
                                }}
                              >
                                <EmailIcon sx={{ fontSize: "16px" }} />
                              </Link>
                            </Tooltip>
                          )}
                          {student.fatherPhoneNo && (
                            <Tooltip title={student.fatherPhoneNo}>
                              <Link
                                href={`tel:${student.fatherPhoneNo}`}
                                sx={{
                                  display: "flex",
                                  cursor: "pointer",
                                  color: "#1976d2",
                                }}
                              >
                                <PhoneIcon sx={{ fontSize: "16px" }} />
                              </Link>
                            </Tooltip>
                          )}
                        </Box>
                      )}

                      {/* Mother's Info */}
                      {student.motherName && (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                          }}
                        >
                          <Tooltip title="Mother">
                            <PersonIcon
                              sx={{ fontSize: "18px", color: "#d32f2f" }}
                            />
                          </Tooltip>
                          <Typography variant="caption">
                            {student.motherName}
                          </Typography>
                          {student.motherEmail && (
                            <Tooltip title={student.motherEmail}>
                              <Link
                                href={`mailto:${student.motherEmail}`}
                                sx={{
                                  display: "flex",
                                  cursor: "pointer",
                                  color: "#d32f2f",
                                }}
                              >
                                <EmailIcon sx={{ fontSize: "16px" }} />
                              </Link>
                            </Tooltip>
                          )}
                          {student.motherPhoneNo && (
                            <Tooltip title={student.motherPhoneNo}>
                              <Link
                                href={`tel:${student.motherPhoneNo}`}
                                sx={{
                                  display: "flex",
                                  cursor: "pointer",
                                  color: "#d32f2f",
                                }}
                              >
                                <PhoneIcon sx={{ fontSize: "16px" }} />
                              </Link>
                            </Tooltip>
                          )}
                        </Box>
                      )}

                      {/* Emergency Contact Info */}
                      {student.emergencyContactName && (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                          }}
                        >
                          <Tooltip title="Emergency Contact">
                            <PersonIcon
                              sx={{ fontSize: "18px", color: "#388e3c" }}
                            />
                          </Tooltip>
                          <Typography variant="caption">
                            {student.emergencyContactName}
                          </Typography>
                          {student.emergencyContactPhoneNo && (
                            <Tooltip title={student.emergencyContactPhoneNo}>
                              <Link
                                href={`tel:${student.emergencyContactPhoneNo}`}
                                sx={{
                                  display: "flex",
                                  cursor: "pointer",
                                  color: "#388e3c",
                                }}
                              >
                                <PhoneIcon sx={{ fontSize: "16px" }} />
                              </Link>
                            </Tooltip>
                          )}
                        </Box>
                      )}
                    </Box>
                  </TableCell>

                  <TableCell>
                    <Box
                      sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                    >
                      {student.foodAllergies && (
                        <Typography variant="body2">
                          <strong>Food:</strong> {student.foodAllergies}
                        </Typography>
                      )}
                      {student.hasAllergicReactionAsthmaAnaphylaxis && (
                        <Typography
                          variant="body2"
                          sx={{ color: "red", fontWeight: "bold" }}
                        >
                          ⚠️ Allergic Reaction / Anaphylaxis Risk
                        </Typography>
                      )}
                      {student.allergyNotes && (
                        <Typography variant="body2">
                          <strong>Notes:</strong> {student.allergyNotes}
                        </Typography>
                      )}
                      {!student.foodAllergies &&
                        !student.hasAllergicReactionAsthmaAnaphylaxis &&
                        !student.allergyNotes && (
                          <Typography variant="body2" sx={{ color: "#999" }}>
                            No allergies recorded
                          </Typography>
                        )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Container>
  );
};

export default Allergies;
